import { AppointmentState } from "../types";
import { getVehicleId } from "./formatting";
import {
  validateCityState,
  validateEmail,
  validateName,
  validateNumber,
  validateZip,
} from "./validation";
import { BookingStage } from "../constants/bookingStages";

export const isStage1NextEnabled = (appointment: AppointmentState): boolean => {
  return Boolean(appointment?.services?.length);
};

export const isStage2NextEnabled = (appointment: AppointmentState): boolean =>
  isStage2NextEnabledWithComments(appointment);

export const isStage2SelectionNextEnabled = (
  appointment: AppointmentState
): boolean => {
  const hasApptType = Boolean(appointment?.apptType);
  const hasDate = Boolean(appointment?.date);
  const hasTime = Boolean(appointment?.time);
  const shop = appointment?.shopData || ({} as any);
  const hasTransport = ["shuttleEnabled", "loanerEnabled", "rentalEnabled"].some(
    (flag) => shop[flag as keyof typeof shop]
  );
  const requiresRide = hasTransport && appointment?.apptType === "drop";
  const hasRide = !requiresRide || Boolean(appointment?.complimentaryRide);
  return hasApptType && hasDate && hasTime && hasRide;
};

export const isStage2NextEnabledWithComments = (
  appointment: AppointmentState
): boolean => {
  const isSelectionComplete = isStage2SelectionNextEnabled(appointment);
  const requiresComments = Boolean(appointment?.shopData?.requireComments);
  const hasComments = !requiresComments || Boolean((appointment?.comments || "").trim());
  return isSelectionComplete && hasComments;
};

export const isStage3NextEnabled = (appointment: AppointmentState): boolean => {
  return Boolean(
    appointment?.customer?.phone && validateNumber(appointment.customer.phone)
  );
};

export const isStage4ConfirmEnabled = (appointment: AppointmentState): boolean => {
  const customer = (appointment?.customer as any) || {};
  const address = customer?.address || {};
  const fname = customer?.fname?.trim?.() || "";
  const lname = customer?.lname?.trim?.() || "";
  const email = customer?.email?.trim?.() || "";
  const street = address?.street?.trim?.() || "";
  const city = address?.city?.trim?.() || "";
  const state = address?.state?.trim?.() || "";
  const zip = address?.zip?.trim?.() || "";

  // Names hydrated from an existing-customer lookup and not edited this session
  // are trusted as-is — a stored name is not re-checked against the input-format
  // regex (empty checks still apply). Address fields are not trusted this way;
  // a format-failing stored value is blanked at hydration and re-collected.
  const prefilled = new Set(appointment?.prefilledCustomerFields || []);
  const skipFormat = (field: string) => prefilled.has(field);

  const hasFname = Boolean(fname) && (skipFormat("fname") || validateName(fname));
  const hasLname = Boolean(lname) && (skipFormat("lname") || validateName(lname));
  const requireEmail =
    Boolean(appointment?.shopData?.requireNewCustomerEmail) ||
    Boolean(appointment?.requireValidCustomerEmail);
  const hasEmail = requireEmail
    ? Boolean(email) && validateEmail(email)
    : validateEmail(email);
  const requireAddress = Boolean(appointment?.shopData?.requireAddress);
  const hasAddress = !requireAddress || Boolean(
    street &&
      city &&
      state &&
      zip &&
      validateCityState(city) &&
      validateCityState(state) &&
      validateZip(zip)
  );
  const hasVehicle = Boolean(
    (appointment as any)?.vehicle?.year &&
      (appointment as any)?.vehicle?.make &&
      (appointment as any)?.vehicle?.model
  );

  const declinedMakes = ((appointment?.shopData as any)?.declinedMake || "")
    .split(",")
    .map((m: string) => m.trim().toLowerCase())
    .filter(Boolean);
  const declinedModels = ((appointment?.shopData as any)?.declinedModel || "")
    .split(",")
    .map((m: string) => m.trim().toLowerCase())
    .filter(Boolean);
  const vehicleMake = ((appointment as any)?.vehicle?.make || "").toLowerCase();
  const vehicleModel = ((appointment as any)?.vehicle?.model || "").toLowerCase();

  // Vehicles chosen from the customer's existing garage carry a shop-system
  // crossRef (the same signal the submit payload uses for vehicleId; every
  // CarPicker path writes crossRef: []). Garage vehicles are exempt from the
  // serviceability rules — declined make/model and oldest year only gate
  // newly entered vehicles.
  const fromGarage = getVehicleId((appointment as any)?.vehicle) !== null;
  const makeDeclined = !fromGarage && declinedMakes.includes(vehicleMake);
  const modelDeclined = !fromGarage && declinedModels.includes(vehicleModel);

  const oldestYear = (appointment?.shopData as any)?.oldestYearServicable;
  const vehicleYear = (appointment as any)?.vehicle?.year;
  const yearTooOld =
    !fromGarage &&
    Boolean(oldestYear) &&
    Boolean(vehicleYear) &&
    Number(vehicleYear) < Number(oldestYear);

  return (
    hasFname &&
    hasLname &&
    hasEmail &&
    hasAddress &&
    hasVehicle &&
    !makeDeclined &&
    !modelDeclined &&
    !yearTooOld
  );
};

export const getStage4DisabledReasons = (
  appointment: AppointmentState
): string[] => {
  const customer = (appointment?.customer as any) || {};
  const address = customer?.address || {};
  const fname = customer?.fname?.trim?.() || "";
  const lname = customer?.lname?.trim?.() || "";
  const email = customer?.email?.trim?.() || "";
  const street = address?.street?.trim?.() || "";
  const city = address?.city?.trim?.() || "";
  const state = address?.state?.trim?.() || "";
  const zip = address?.zip?.trim?.() || "";

  const reasons: string[] = [];

  // Mirror isStage4ConfirmEnabled: prefilled-and-unedited names skip the
  // name regex (stored names are trusted; address fields are not).
  const prefilled = new Set(appointment?.prefilledCustomerFields || []);
  const skipFormat = (field: string) => prefilled.has(field);

  if (!fname) reasons.push("customer.fname is empty");
  else if (!skipFormat("fname") && !validateName(fname))
    reasons.push(`customer.fname "${fname}" fails name regex`);

  if (!lname) reasons.push("customer.lname is empty");
  else if (!skipFormat("lname") && !validateName(lname))
    reasons.push(`customer.lname "${lname}" fails name regex`);

  const requireEmail =
    Boolean(appointment?.shopData?.requireNewCustomerEmail) ||
    Boolean(appointment?.requireValidCustomerEmail);
  if (requireEmail && !email) reasons.push("customer.email is required and empty");
  else if (email && !validateEmail(email))
    reasons.push(`customer.email "${email}" fails email regex`);

  if (appointment?.shopData?.requireAddress) {
    if (!street) reasons.push("customer.address.street is empty");
    if (!city) reasons.push("customer.address.city is empty");
    else if (!validateCityState(city))
      reasons.push(`customer.address.city "${city}" fails name regex`);
    if (!state) reasons.push("customer.address.state is empty");
    else if (!validateCityState(state))
      reasons.push(`customer.address.state "${state}" fails name regex`);
    if (!zip) reasons.push("customer.address.zip is empty");
    else if (!validateZip(zip))
      reasons.push(`customer.address.zip "${zip}" fails zip regex`);
  }

  const vehicle = (appointment as any)?.vehicle || {};
  if (!vehicle.year) reasons.push("vehicle.year is missing");
  if (!vehicle.make) reasons.push("vehicle.make is missing");
  if (!vehicle.model) reasons.push("vehicle.model is missing");

  const declinedMakes = ((appointment?.shopData as any)?.declinedMake || "")
    .split(",")
    .map((m: string) => m.trim().toLowerCase())
    .filter(Boolean);
  const declinedModels = ((appointment?.shopData as any)?.declinedModel || "")
    .split(",")
    .map((m: string) => m.trim().toLowerCase())
    .filter(Boolean);
  const vehicleMake = (vehicle.make || "").toLowerCase();
  const vehicleModel = (vehicle.model || "").toLowerCase();
  // Mirror isStage4ConfirmEnabled: garage vehicles (crossRef present) are
  // exempt from the declined make/model and oldest-year rules.
  const fromGarage = getVehicleId(vehicle) !== null;
  if (!fromGarage && declinedMakes.includes(vehicleMake))
    reasons.push(`vehicle.make "${vehicle.make}" is in shopData.declinedMake`);
  if (!fromGarage && declinedModels.includes(vehicleModel))
    reasons.push(`vehicle.model "${vehicle.model}" is in shopData.declinedModel`);

  const oldestYear = (appointment?.shopData as any)?.oldestYearServicable;
  if (
    !fromGarage &&
    Boolean(oldestYear) &&
    Boolean(vehicle.year) &&
    Number(vehicle.year) < Number(oldestYear)
  ) {
    reasons.push(
      `vehicle.year ${vehicle.year} is older than shopData.oldestYearServicable ${oldestYear}`
    );
  }

  return reasons;
};

export const isNextEnabledForStage = (
  stage: number | undefined,
  appointment: AppointmentState
): boolean => {
  if (stage === BookingStage.ServiceSelection) return isStage1NextEnabled(appointment);
  if (stage === BookingStage.AppointmentOptions) return isStage2NextEnabled(appointment);
  if (stage === BookingStage.CustomerInfo) return isStage3NextEnabled(appointment);
  return isStage4ConfirmEnabled(appointment);
};

export const getNextStage = (appointment: AppointmentState): number => {
  const isConfirmStage = appointment?.stage === BookingStage.VehicleInfo;
  if (isConfirmStage && appointment?.skipToForm) return BookingStage.SkipToForm;
  if (isConfirmStage) return BookingStage.Submitting;
  if (appointment?.skipToForm) return BookingStage.SkipToForm;
  return (appointment?.stage ?? BookingStage.ServiceSelection) + 1;
};
